import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, extends as _extends } from './_virtual/_rollupPluginBabelHelpers.js';
import { utils } from '@costar/theme-ui';
import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import Autocomplete from './autocomplete.esm.js';
import defaultGetOptionLabel from './helpers/default-get-option-label.esm.js';
import { lastPromise } from './helpers/last-promise.esm.js';

var _excluded = ["minimumInputLength", "maximumInputLength", "options", "getOptions", "getOptionLabel", "onInputChange", "onFocus", "inputValue", "debounceTime", "onBlur"];
var DEFAULT_DEBOUNCE_TIME = 50;
var EMPTY_OPTIONS = [];

//@ts-ignore
var _AsyncAutocomplete = /*#__PURE__*/React.forwardRef(function AsyncAutocomplete(props, ref) {
  var _componentsProps$inpu5;
  var getOptionsLastPromise = useMemo(function () {
    return lastPromise();
  }, []);
  var minimumInputLength = props.minimumInputLength,
    maximumInputLength = props.maximumInputLength,
    _props$options = props.options,
    initialOptions = _props$options === void 0 ? EMPTY_OPTIONS : _props$options,
    getOptions = props.getOptions,
    _props$getOptionLabel = props.getOptionLabel,
    getOptionLabel = _props$getOptionLabel === void 0 ? defaultGetOptionLabel : _props$getOptionLabel,
    onInputChange = props.onInputChange,
    onFocus = props.onFocus,
    inputValue = props.inputValue,
    _props$debounceTime = props.debounceTime,
    debounceTime = _props$debounceTime === void 0 ? DEFAULT_DEBOUNCE_TIME : _props$debounceTime,
    onBlur = props.onBlur,
    rest = _objectWithoutProperties(props, _excluded);
  var _useState = useState(initialOptions),
    _useState2 = _slicedToArray(_useState, 2),
    options = _useState2[0],
    setOptions = _useState2[1];
  var optionsRef = useRef(initialOptions); //this never gets updated after init

  useEffect(function () {
    return setOptions(initialOptions);
  }, [initialOptions]);
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    fetching = _useState4[0],
    isFetching = _useState4[1];
  var inputValueRef = useRef(inputValue);
  var getAndSetOptions = useCallback(function (value) {
    getOptionsLastPromise(getOptions(value)).then(function (_ref) {
      var result = _ref.result,
        isStale = _ref.isStale;
      if (!isStale) {
        isFetching(false);
        if (inputValueRef.current === value) {
          setOptions(result);
        }
      }
    })["catch"](function (e) {
      // eslint-disable-next-line no-console
      console.log('Encountered an error in retieving suggestions ', e);
      isFetching(false);
    });
  }, [getOptions, getOptionsLastPromise]);
  var debouncedSuggestionReq = useMemo(function () {
    return utils.debounce(getAndSetOptions, debounceTime);
  }, [getAndSetOptions, debounceTime]);
  var makeFetchCall = useCallback(function (value) {
    var trimmedValueLength = value === null || value === void 0 ? void 0 : value.trim().length;
    if (trimmedValueLength >= (minimumInputLength || 0) && trimmedValueLength <= (maximumInputLength || 500)) {
      isFetching(true);
      debouncedSuggestionReq(value);
    }
  }, [debouncedSuggestionReq, maximumInputLength, minimumInputLength]);
  var handleInputChange = useCallback(function (event, value, reason) {
    inputValueRef.current = value;
    switch (reason) {
      case 'clear':
        {
          // setVal('')
          break;
        }
      // user input
      case 'input':
        {
          makeFetchCall(value);
          break;
        }
    }
    if (!value) {
      setOptions(initialOptions);
    }
    onInputChange && onInputChange(event, value, reason);
  }, [initialOptions, makeFetchCall, onInputChange]);

  //store value as ref to avoid triggering useEffect
  var valueRef = useRef(props === null || props === void 0 ? void 0 : props.value);
  valueRef.current = props === null || props === void 0 ? void 0 : props.value;

  //if consumer does not provide a ref for input, then we add one here
  // so we can inspect the input value when/if getOptions changes
  var defaultInputRef = useRef(null);
  var componentsProps = useMemo(function () {
    var _props$componentsProp, _props$componentsProp2, _props$componentsProp3;
    if (props !== null && props !== void 0 && (_props$componentsProp = props.componentsProps) !== null && _props$componentsProp !== void 0 && (_props$componentsProp2 = _props$componentsProp.input) !== null && _props$componentsProp2 !== void 0 && _props$componentsProp2.ref) {
      return props === null || props === void 0 ? void 0 : props.componentsProps;
    }
    return _objectSpread2(_objectSpread2({}, props === null || props === void 0 ? void 0 : props.componentsProps), {}, {
      input: _objectSpread2(_objectSpread2({}, props === null || props === void 0 ? void 0 : (_props$componentsProp3 = props.componentsProps) === null || _props$componentsProp3 === void 0 ? void 0 : _props$componentsProp3.input), {}, {
        ref: defaultInputRef
      })
    });
  }, [props === null || props === void 0 ? void 0 : props.componentsProps]);
  var handleBlur = useCallback(function (event) {
    var _componentsProps$inpu;
    isFetching(false);
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    var r = componentsProps === null || componentsProps === void 0 ? void 0 : (_componentsProps$inpu = componentsProps.input) === null || _componentsProps$inpu === void 0 ? void 0 : _componentsProps$inpu.ref;
    setTimeout(function () {
      var _r$current;
      r === null || r === void 0 ? void 0 : (_r$current = r.current) === null || _r$current === void 0 ? void 0 : _r$current.blur();
    }, 1);
  }, [onBlur, componentsProps]);
  var didMountRef = useRef(false);

  //listen to changes in fetcher (if change and we have tabs then we should fire off new request for options)
  //RISK: if getOptions is not correctly memoized, this useEffect could could get called with every render?
  useEffect(function () {
    var _componentsProps$inpu2, _componentsProps$inpu3, _componentsProps$inpu4;
    setOptions(optionsRef.current);
    if (!didMountRef.current) {
      // skipping a fetch call on componentDidMount to avoid a request on page load
      // before the user ever interacted with the package
      didMountRef.current = true;
      return;
    }
    var currentValueInInput = (_componentsProps$inpu2 = componentsProps.input) === null || _componentsProps$inpu2 === void 0 ? void 0 : (_componentsProps$inpu3 = _componentsProps$inpu2.ref) === null || _componentsProps$inpu3 === void 0 ? void 0 : (_componentsProps$inpu4 = _componentsProps$inpu3.current) === null || _componentsProps$inpu4 === void 0 ? void 0 : _componentsProps$inpu4.value;
    if (currentValueInInput) {
      // @ts-ignore -- FIXME(don): T is not restrictive enough
      makeFetchCall(getOptionLabel(currentValueInInput));
    }
  }, [getOptions, makeFetchCall, getOptionLabel, optionsRef, (_componentsProps$inpu5 = componentsProps.input) === null || _componentsProps$inpu5 === void 0 ? void 0 : _componentsProps$inpu5.ref]);

  //on focus event:
  // 1. fire off a suggestion req if a value has been provided
  var onFocusWithFetchForOptions = useCallback(function (e) {
    if (valueRef !== null && valueRef !== void 0 && valueRef.current && (options === null || options === void 0 ? void 0 : options.length) <= 0) {
      // @ts-ignore -- FIXME(don): T is not restrictive enough
      makeFetchCall(getOptionLabel(valueRef === null || valueRef === void 0 ? void 0 : valueRef.current));
    }
    onFocus && (onFocus === null || onFocus === void 0 ? void 0 : onFocus(e));
  }, [onFocus, getOptionLabel, makeFetchCall, options]);
  return /*#__PURE__*/React.createElement(Autocomplete, _extends({
    ref: ref
    // @ts-ignore -- FIXME(don): T is not restrictive enough
    ,
    options: options,
    loading: fetching
    // @ts-ignore -- FIXME(don): T is not restrictive enough
    ,
    getOptionLabel: getOptionLabel,
    onBlur: handleBlur,
    minimumInputLength: minimumInputLength,
    maximumInputLength: maximumInputLength,
    onInputChange: handleInputChange,
    onFocus: onFocusWithFetchForOptions,
    shouldCheckIsOptionEqualToValue: false,
    inputValue: inputValue,
    componentsProps: componentsProps
  }, rest));
});
function AsyncAutocomplete(props) {
  return /*#__PURE__*/React.createElement(_AsyncAutocomplete, props);
}

export default AsyncAutocomplete;
//# sourceMappingURL=async-autocomplete.esm.js.map
